import "./SupportPage.css";
import { useContext, useEffect, useMemo, useState } from "react";

import SupportCategories from "./SupportCategories/SupportCategories";
import SupportForm from "./SupportForm/SupportForm";
import SupportSuccess from "./SupportSuccess/SupportSuccess";

import { getCategoriesAPI, createSupportAPI } from "../../../utils/api/support";

import {
  buildCustomFields,
  getFieldValue,
  getText,
  normalizeCategories,
} from "./helpers/supportHelpers";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";

const TEXT = {
  ar: {
    pageTitle: "رفع تذكرة دعم فني",
    sent: "تم إرسال الطلب بنجاح",
    loadFailed: "تعذر تحميل أنواع الطلبات.",
    submitFailed: "تعذر إرسال الطلب. حاول مرة أخرى.",
  },
  en: {
    pageTitle: "Create Support Ticket",
    sent: "Request sent successfully",
    loadFailed: "Request types could not be loaded.",
    submitFailed: "The request could not be sent. Please try again.",
  },
};

// Categories branch users may raise tickets for.
export const CATEGORY_CODES = [
  "app_issue",
  "menu_update",
  "working_hours",
  "rider_issue",
  "other",
];

const SupportPage = () => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const branchOnly = can("support.category.branchOnly");
  const t = TEXT[lang] || TEXT.ar;

  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successTicket, setSuccessTicket] = useState(null);

  // Derived instead of filtered in an effect: the user's access can arrive
  // after the categories, and the effect re-filtered a stale snapshot.
  const categories = useMemo(() => {
    if (!branchOnly) return allCategories;
    const allowed = new Set(CATEGORY_CODES);
    return allCategories.filter((d) => allowed.has(d.code));
  }, [allCategories, branchOnly]);

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("auth-token");
    if (!token) return undefined;

    setLoading(true);
    getCategoriesAPI(token)
      .then((res) => {
        if (active) setAllCategories(normalizeCategories(res));
      })
      .catch((error) => {
        console.error(error);
        if (active) setLoadError(t.loadFailed);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // Load once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Throws nothing: failures are shown in the form, which stays filled.
  const handleSubmitSupport = async (formValues, storeId, branchId) => {
    if (submitLoading) return;
    setSubmitLoading(true);
    setSubmitError("");
    try {
      const token = localStorage.getItem("auth-token");

      const title =
        getFieldValue(formValues, [
          "title",
          "subject",
          "request_title",
          "topic",
        ]) || getText(selectedCategory?.name, lang);

      const description =
        getFieldValue(formValues, [
          "description",
          "problem_description",
          "issue_description",
          "details",
          "notes",
          "note",
          "reason",
        ]) ||
        getText(selectedCategory?.description, lang) ||
        title;

      const app =
        getFieldValue(formValues, [
          "app",
          "application",
          "app_name",
          "platform",
        ]) || null;

      const orderNumber =
        getFieldValue(formValues, [
          "orderNumber",
          "order_number",
          "order_id",
          "orderId",
        ]) || null;

      const body = {
        title,
        description,
        storeId,
        branchId,
        category: selectedCategory?.code,
        priority: "normal",
        app,
        orderNumber,
        tags: [],
        customFields: buildCustomFields(formValues),
      };

      const res = await createSupportAPI(token, body);

      setSuccessTicket(
        res?.data?.ticketNumber ||
          res?.data?._id ||
          res?.ticketNumber ||
          res?._id ||
          t.sent,
      );

      setSelectedCategory(null);
    } catch (error) {
      console.error(error);
      setSubmitError(error?.message || t.submitFailed);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <SupportSuccess ticketNumber={successTicket} lang={lang} />

      <div className="support-page-header">
        <h1>{t.pageTitle}</h1>
      </div>

      {loadError && (
        <p className="support-action-error" role="alert">
          {loadError}
        </p>
      )}

      <SupportCategories
        categories={categories}
        loading={loading}
        selectedCategory={selectedCategory}
        onSelectCategory={(category) => {
          setSelectedCategory(category);
          setSuccessTicket(null);
          setSubmitError("");
        }}
        lang={lang}
      />

      {selectedCategory && (
        <SupportForm
          category={selectedCategory}
          loading={submitLoading}
          error={submitError}
          onCancel={() => {
            setSelectedCategory(null);
            setSubmitError("");
          }}
          onSubmit={handleSubmitSupport}
          lang={lang}
        />
      )}
    </>
  );
};

export default SupportPage;
